import UserSelector from "./UserSelector";
import { cn } from "app/lib/utils";
import SelectedUserBox from "./SelectedUserBox";

interface Props {
  title: string;
  description?: JSX.Element | string | null;
  searchInputText?: string;
  selectedUserAccountId?: number | null;
  setSelectedUserAccountId?: (userAccountId: number | null) => void;
  isAlternativeOptionSelected?: boolean;
  labels: {
    primaryLabel: {
      title: string;
      description?: string;
    };
    secondaryLabel: {
      title: string;
      description?: string;
    };
  };
  onAlternativeOptionSelected?: (isAlternativeOptionSelected: boolean) => void;
  error?: {
    errorMessage: string;
  };
  excludeUserAccountIds?: number[];
}

const ReassignmentBox = ({
  title,
  description = null,
  selectedUserAccountId,
  setSelectedUserAccountId,
  isAlternativeOptionSelected,
  labels,
  onAlternativeOptionSelected,
  error,
  excludeUserAccountIds: excludeUserAccountIdsProp = [],
}: Readonly<Props>) => {
  const getUserSelectedElement = () => {
    if (!selectedUserAccountId) return null;
    return (
      <SelectedUserBox
        userAccountId={selectedUserAccountId}
        onRemoveClick={() => {
          setSelectedUserAccountId?.(null);
        }}
      />
    );
  };
  const getUserSelectionElement = () => {
    if (isAlternativeOptionSelected) {
      return null;
    }
    const excludeUserAccountIds: number[] = [];
    if (selectedUserAccountId) {
      excludeUserAccountIds.push(selectedUserAccountId);
    }
    excludeUserAccountIds.push(...excludeUserAccountIdsProp);

    return (
      <div className="flex flex-col gap-2 my-[8px]">
        <UserSelector
          selectedUserAccountId={selectedUserAccountId}
          onUserSelected={setSelectedUserAccountId}
          placeholderText="Select a user"
          excludeUserAccountIds={excludeUserAccountIds}
        />
        {getUserSelectedElement()}
      </div>
    );
  };

  const renderOptionSelector = (
    isSelected: boolean,
    onSelect: () => void,
    title: string,
    description?: string
  ) => {
    return (
      <button
        onClick={onSelect}
        className={cn(
          `w-full flex items-start gap-3 p-4 border rounded-lg transition-colors`,
          isSelected
            ? "bg-blue-50 border-blue-600"
            : "bg-white border-gray-200 hover:bg-gray-50"
        )}
      >
        <div className="mt-1">
          <div
            className={cn(
              `h-5 w-5 rounded-full flex items-center justify-center`,
              isSelected ? "bg-blue-600" : "border border-gray-300"
            )}
          >
            {isSelected && (
              <svg
                className="h-3 w-3 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
        </div>
        <div className="flex flex-col text-left">
          <p className="font-medium text-gray-900">{title}</p>
          {description && (
            <p className="text-sm text-gray-500 mt-1">{description}</p>
          )}
        </div>
      </button>
    );
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-2 bg-gray-50 rounded-lg p-4 shadow-sm border border-gray-200",
        error && "border-red-500 border-[2px]"
      )}
    >
      <div className="flex items-center gap-2">
        <h4>{title}</h4>
        {error && <p className="text-red-500">{error.errorMessage}</p>}
      </div>
      {description && <p>{description}</p>}
      {renderOptionSelector(
        !isAlternativeOptionSelected,
        () => {
          onAlternativeOptionSelected?.(false);
        },
        labels?.primaryLabel.title,
        labels?.primaryLabel.description
      )}
      {getUserSelectionElement()}
      {renderOptionSelector(
        !!isAlternativeOptionSelected,
        () => {
          onAlternativeOptionSelected?.(true);
        },
        labels?.secondaryLabel?.title,
        labels?.secondaryLabel?.description
      )}
    </div>
  );
};

export default ReassignmentBox;
